import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  message,
  Button,
  Select,
  Upload,
  Space,
  Row,
  Col,
  Card,
} from 'antd';
import { useTranslation } from '@refinedev/core';
import useApi from '@/api/useApi';
import { endPoints } from '@/api/configs';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

const { TextArea } = Input;

interface AddSectionModalProps {
  open: boolean;
  onCancel: () => void;
  refetch: () => void;
  postId: string;
}

interface UploadItem {
  uid: string;
  file: File;
  description: string;
  view_side?: string;
}

interface UploadState {
  fileList: UploadFile[];
  uploads: UploadItem[];
}

export const AddSectionModal: React.FC<AddSectionModalProps> = ({
  open,
  onCancel,
  refetch,
  postId,
}) => {
  const { translate } = useTranslation();
  const { usePost } = useApi();
  const [form] = Form.useForm();
  const [uploadsState, setUploadsState] = useState<UploadState>({
    fileList: [],
    uploads: [],
  });

  // Section creation mutation with files
  const { mutate: createSection, isLoading: isCreating } = usePost(
    endPoints.addSection(postId),
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  const handleUpload = (file: File) => {
    const uid = Date.now().toString();
    setUploadsState((prev) => ({
      fileList: [
        ...prev.fileList,
        {
          uid,
          name: file.name,
          status: 'done',
          url: URL.createObjectURL(file),
        },
      ],
      uploads: [
        ...prev.uploads,
        {
          uid,
          file,
          description: '',
          view_side: undefined,
        },
      ],
    }));

    return false; // Prevent default upload behavior
  };

  const handleRemove = (file: UploadFile) => {
    setUploadsState((prev) => ({
      fileList: prev.fileList.filter((item) => item.uid !== file.uid),
      uploads: prev.uploads.filter((item) => item.uid !== file.uid),
    }));
  };

  const updateUploadMetadata = (
    uid: string,
    field: 'description' | 'view_side',
    value: string
  ) => {
    setUploadsState((prev) => ({
      ...prev,
      uploads: prev.uploads.map((item) =>
        item.uid === uid ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        // Create FormData to handle file uploads
        const formData = new FormData();

        // Add section data
        formData.append('type', values.type);
        formData.append('view', values.view);
        formData.append('title', values.title);

        if (values.content) formData.append('content', values.content);
        if (values.quote) formData.append('quote', values.quote);
        if (values.description)
          formData.append('description', values.description);

        // Add uploads with metadata
        uploadsState.uploads.forEach((upload, index) => {
          formData.append(`uploads[${index}][file]`, upload.file);
          formData.append(`uploads[${index}][description]`, upload.description);
          if (upload.view_side) {
            formData.append(`uploads[${index}][view_side]`, upload.view_side);
          }
        });

        createSection(formData, {
          onSuccess: () => {
            message.success(translate('section.created-successfully'));
            form.resetFields();
            setUploadsState({ fileList: [], uploads: [] });
            refetch();
            onCancel();
          },
          onError: (error) => {
            message.error(error.message || translate('section.error-creating'));
          },
        });
      })
      .catch(() => {
        message.error(translate('pages.errors.validation'));
      });
  };

  const handleCancel = () => {
    form.resetFields();
    setUploadsState({ fileList: [], uploads: [] });
    onCancel();
  };

  return (
    <Modal
      title={translate('section.add-section')}
      open={open}
      footer={null}
      onCancel={handleCancel}
      width={800}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="type"
          label={translate('section.type')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Select>
            <Select.Option value="TEXT">
              {translate('section.text')}
            </Select.Option>
            <Select.Option value="MEDIA_DESCRIPTION">
              {translate('section.media-description')}
            </Select.Option>
            <Select.Option value="MEDIA_TEXT">
              {translate('section.media-text')}
            </Select.Option>
            <Select.Option value="MEDIA_QUOTE">
              {translate('section.media-quote')}
            </Select.Option>
            <Select.Option value="TWO_MEDIA">
              {translate('section.two-media')}
            </Select.Option>
            <Select.Option value="TEXT_QUOTE">
              {translate('section.text-quote')}
            </Select.Option>
            <Select.Option value="EMBED">
              {translate('section.embed')}
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="view"
          label={translate('section.view')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Select>
            <Select.Option value="VERTICAL">VERTICAL</Select.Option>
            <Select.Option value="HORIZONTAL">HORIZONTAL</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="title"
          label={translate('section.title')}
          rules={[
            { required: true, message: translate('pages.errors.required') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="content" label={translate('section.content')}>
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item name="quote" label={translate('section.quote')}>
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item name="description" label={translate('section.description')}>
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item label={translate('section.uploads')}>
          <Upload
            listType="picture"
            fileList={uploadsState.fileList}
            beforeUpload={handleUpload}
            onRemove={handleRemove}
            multiple
            accept=".png,.jpg,.jpeg,.gif,.mp4"
          >
            <Button icon={<UploadOutlined />}>
              {translate('buttons.upload')}
            </Button>
          </Upload>
        </Form.Item>
        {uploadsState.uploads.length > 0 && (
          <div className="mb-4">
            <h4>{translate('section.upload-details')}</h4>
            {uploadsState.uploads.map((upload) => (
              <Card key={upload.uid} className="mb-2">
                <Row gutter={16} align="middle">
                  <Col span={8}>
                    <p className="mb-1">{upload.file.name}</p>
                  </Col>
                  <Col span={8}>
                    <Input
                      placeholder={translate('section.description')}
                      value={upload.description}
                      onChange={(e) =>
                        updateUploadMetadata(
                          upload.uid,
                          'description',
                          e.target.value
                        )
                      }
                    />
                  </Col>
                  <Col span={7}>
                    <Select
                      placeholder={translate('section.view-side')}
                      value={upload.view_side}
                      onChange={(value) =>
                        updateUploadMetadata(upload.uid, 'view_side', value)
                      }
                      allowClear
                      style={{ width: '100%' }}
                    >
                      <Select.Option value="LEFT">
                        {translate('section.left')}
                      </Select.Option>
                      <Select.Option value="RIGHT">
                        {translate('section.right')}
                      </Select.Option>
                    </Select>
                  </Col>
                  <Col span={1}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        handleRemove({ uid: upload.uid } as UploadFile)
                      }
                    />
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        )}
        <Space className="flex justify-end">
          <Button onClick={handleCancel}>{translate('buttons.cancel')}</Button>
          <Button type="primary" htmlType="submit" loading={isCreating}>
            {translate('buttons.add')}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};
